import { FORM_BIT, REACTIONS, SKILLS, UNIQUE_BOSSES, UNIQUE_FORMS, UNIQUES, NIGHTMARES, HEROES, type SkillId, type UniqueId } from '@somnhold/content';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { cosA, dist2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Hero, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, isFlying, targetable, wardHero } from './combat.ts';
import { raiseShield } from './kits.ts';
import { stunFor } from './towers.ts';

/**
 * The forms of the unique pieces in a fight (docs/UNICAS.md §3; numbers in content `UNIQUE_FORMS`). The two forms of
 * stage II and the final one of stage IV each change one thing of the piece's power. A hero carries them as bits
 * (`Hero.uqForm`, parallel to `uq`, worked out by the campaign); without them nothing here runs, so a fight without
 * forms plays out as before. The forms of the skills hook into their programs (`content/skill-casts.ts`, the steps
 * `when: { form }`, and the specials in `cast.ts`); the rest are here, called from the code they change in one line.
 *
 * What a form leaves on the ground is a zone of its own kind (`cleft`, `fireLine`, `toll`, `oldRoot`, `parapet`,
 * `puddle`, `vigil`, `sprout`) or a mark on a zone (`fm`, the form bits of the piece behind it); what it calls is a
 * summon of its own (`gasparGuard`, `cloak`, `barkBear`). Each one is seen (docs/UNICAS.md §6).
 */

const BOSSES: ReadonlySet<string> = new Set<string>(UNIQUE_BOSSES);

/** The form bits a hero carries a unique piece in (0: none, or not worn). */
export const formBits = (h: Hero | null | undefined, id: UniqueId): number => {
  const f = h?.uqForm;
  if (!f) return 0;
  const i = (h.uq as UniqueId[]).indexOf(id);
  return i >= 0 ? (f[i] ?? 0) : 0;
};
export const formA = (h: Hero | null | undefined, id: UniqueId): boolean => (formBits(h, id) & FORM_BIT.a) !== 0;
export const formB = (h: Hero | null | undefined, id: UniqueId): boolean => (formBits(h, id) & FORM_BIT.b) !== 0;
export const formFinal = (h: Hero | null | undefined, id: UniqueId): boolean => (formBits(h, id) & FORM_BIT.final) !== 0;

const A = (id: UniqueId): Record<string, number> => UNIQUE_FORMS[id].a?.fx ?? {};
const B = (id: UniqueId): Record<string, number> => UNIQUE_FORMS[id].b?.fx ?? {};
const FIN = (id: UniqueId): Record<string, number> => UNIQUE_FORMS[id].final.fx;
const n = (r: Record<string, number>, k: string): number => r[k] as number;

/**
 * What is a boss for the forms (docs/UNICAS.md §3): the one list in content (`UNIQUE_BOSSES`), the Unsleeping, and
 * whatever this fight marks as its boss (an act's, a crypt's).
 */
export const formBoss = (s: BattleState, e: Enemy): boolean => BOSSES.has(e.kind) || e.un !== undefined || e.id === s.bossId || (s.dungeon?.boss ?? 0) === e.id;

/** Something a form may move or hold on its own terms: not a bone wall, not a siege engine. The dragon is the dragon's fight's (immovable). */
const holdable = (e: Enemy): boolean => e.kind !== 'boneWall' && e.kind !== 'dragon' && !NIGHTMARES[e.kind].siege;

const heroById = (s: BattleState, id: number): Hero | undefined => s.heroes.find((h) => h.id === id);

/** Moves a nightmare to (x, y) when it may stand there (a flyer anywhere). */
const moveTo = (s: BattleState, e: Enemy, x: number, y: number): boolean => {
  if (!isFlying(e) && !heroWalkable(s, cellAtWorld(s, x, y))) return false;
  e.x = x;
  e.y = y;
  e.next = -1;
  return true;
};

/** Whether a point lies within `w` of the segment a–b (fixed units). */
const onSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number, w: number): boolean => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  if (t < 0) t = 0;
  if (t > 1000) t = 1000;
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000)) <= w * w;
};

/** The targetable nightmares within `w` of a segment. */
const onLine = (s: BattleState, ax: number, ay: number, bx: number, by: number, w: number, ground = false): Enemy[] =>
  enemiesNear(s, idiv(ax + bx, 2), idiv(ay + by, 2), idiv(isqrt(dist2(ax, ay, bx, by)), 2) + w, (o) => targetable(s, o) && (!ground || !isFlying(o))).filter((o) => onSegment(o.x, o.y, ax, ay, bx, by, w));

const zoneOf = (s: BattleState, h: { pid: string }, z: Partial<Zone> & Pick<Zone, 'kind' | 'x' | 'y' | 'radius' | 'ticks'>): Zone => {
  const out = { id: s.nextId++, cells: [], every: 1, damage: 0, slowBp: 0, pid: h.pid, ...z } as Zone;
  s.zones.push(out);
  return out;
};

const chill = (s: BattleState, e: Enemy): void => {
  e.chill = s.tick + REACTIONS.chillTicks;
};

/* ———————————————————————— weapons ———————————————————————— */

/** The gate blade's wave: what the bash reached and didn't stun whole is thrown back a hex from the warrior; bosses never. */
export const gateWave = (s: BattleState, h: Hero, hits: Enemy[], stun: number, at: { x: number; y: number }): void => {
  const push = n(A('gateBlade'), 'push');
  for (const e of hits) {
    // The rams too (what no blow stuns is what this form is for); never a bone wall nor the dragon.
    if (e.hp <= 0 || e.kind === 'boneWall' || e.kind === 'dragon' || formBoss(s, e) || e.stun >= stun) continue;
    const d = isqrt(dist2(e.x, e.y, h.x, h.y)) || 1;
    moveTo(s, e, e.x + idiv((e.x - h.x) * push, d), e.y + idiv((e.y - h.y) * push, d));
  }
  ev(s, { t: 'boom', x: at.x, y: at.y, r: n(UNIQUES.gateBlade.fx, 'radius'), kind: 'bashWave' });
};

/** The gate blade's rift: the ground cracks ahead of the blow; whoever crosses it while it lasts is stunned (half). */
export const gateRift = (s: BattleState, h: Hero, e: Enemy, hits: Enemy[], stun: number): void => {
  const f = B('gateBlade');
  const d = isqrt(dist2(e.x, e.y, h.x, h.y)) || 1;
  const L = n(f, 'length');
  zoneOf(s, h, { kind: 'cleft', x: e.x, y: e.y, tx: e.x + idiv((e.x - h.x) * L, d), ty: e.y + idiv((e.y - h.y) * L, d), radius: n(f, 'width'), ticks: n(f, 'ticks'), stun: mulBp(stun, n(f, 'stunBp')), hit: hits.map((o) => o.id) });
};

/** A tick of the rift: the first time each one crosses it, a heavy stun (the big ones whole, elites a third). */
const riftTick = (s: BattleState, z: Zone): void => {
  const hit = (z.hit ??= []);
  for (const e of onLine(s, z.x, z.y, z.tx ?? z.x, z.ty ?? z.y, z.radius, true)) {
    if (hit.includes(e.id)) continue;
    hit.push(e.id);
    const st = heavy(e, z.stun ?? 0);
    if (st > 0 && e.stun < st) {
      e.stun = st;
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 350, kind: 'riftStun' });
    }
  }
};

/** A heavy stun: whole on what stuns and on the big ones, a third on elites, nothing on what nothing stuns. */
const heavy = (e: Enemy, stun: number): number => (e.kind === 'dragon' ? stunFor(e, stun) : e.elite ? (stunFor({ ...e, elite: false }, 3) > 0 ? idiv(stun, 3) : 0) : stunFor(e, 3) > 0 ? stun : 0);

/** The gate shut: the bash stuns bosses too, a moment, once in a while each (never the dragon: its own fight's). */
export const gateShut = (s: BattleState, hits: Enemy[]): void => {
  const f = FIN('gateBlade');
  for (const e of hits) {
    // The dragon of the Burn: never stunned, but the shut gate is a dry blow on its glass (docs/UNICAS.md §4.3).
    if (e.kind === 'dragon' && e.hp > 0) {
      e.dry = true;
      continue;
    }
    // Only the big bosses (UNIQUE_BOSSES): elites and siege engines aren't what the shut gate is for.
    if (e.hp <= 0 || e.kind === 'boneWall' || !BOSSES.has(e.kind)) continue;
    if (e.gateAt !== undefined && s.tick - e.gateAt < n(f, 'every')) continue;
    e.gateAt = s.tick;
    if (e.stun < n(f, 'stun')) e.stun = n(f, 'stun');
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 700, kind: 'gateShut' });
  }
};

/**
 * The garrison key's beam between two turrets (`turretLinks`), its forms: it slows whoever crosses (A), it discharges
 * lightning around now and then (B). `crossed`: who it burnt this time.
 */
export const keyBeam = (s: BattleState, h: Hero, a: { x: number; y: number }, b: { x: number; y: number }, crossed: Enemy[], power: (v: number) => number, beamEvery: number): void => {
  if (formA(h, 'garrisonKey')) {
    const f = A('garrisonKey');
    for (const e of crossed) slowFor(e, n(f, 'slowBp'), n(f, 'ticks'));
  }
  // The beam burns every few ticks (fewer as the piece awakens): it discharges on the first burn past each `every`.
  if (formB(h, 'garrisonKey') && s.tick % n(B('garrisonKey'), 'every') < beamEvery) {
    const f = B('garrisonKey');
    const mx = idiv(a.x + b.x, 2);
    const my = idiv(a.y + b.y, 2);
    const near = enemiesNear(s, mx, my, n(f, 'radius'), (o) => targetable(s, o))
      .sort((p, q) => dist2(p.x, p.y, mx, my) - dist2(q.x, q.y, mx, my) || p.id - q.id)
      .slice(0, n(f, 'max'));
    for (const e of near) {
      damageEnemy(s, e, power(n(f, 'damage')), { pid: h.pid, type: 'lightning', ignoresArmor: true });
      ev(s, { t: 'beam', x: mx, y: my, tx: e.x, ty: e.y, kind: 'discharge' });
    }
  }
};

const slowFor = (e: Enemy, bp: number, ticks: number): void => {
  const sl = e.elite || NIGHTMARES[e.kind].big || NIGHTMARES[e.kind].siege ? idiv(bp, 2) : bp;
  if (e.slowBp < sl) e.slowBp = sl;
  if (e.slowTicks < ticks) e.slowTicks = ticks;
};

/** Three of the artificer's turrets joined: with the hold fast, the ones joined wear out at half the pace. */
export const keyHoldFast = (s: BattleState, h: Hero, link: number, every: number): void => {
  const mine = s.turrets.filter((o) => o.owner === h.id && !o.portable);
  const held = new Set<number>();
  for (let i = 0; i < mine.length; i++)
    for (let j = i + 1; j < mine.length; j++)
      for (let k = j + 1; k < mine.length; k++) {
        const a = mine[i]!;
        const b = mine[j]!;
        const c = mine[k]!;
        if (!withinRange(a.x, a.y, b.x, b.y, link) || !withinRange(b.x, b.y, c.x, c.y, link) || !withinRange(a.x, a.y, c.x, c.y, link)) continue;
        held.add(a.id).add(b.id).add(c.id);
      }
  if (!held.size) return;
  // `every` ticks went by since the last look: a share of them back.
  const back = every - mulBp(every, n(FIN('garrisonKey'), 'rateBp'));
  for (const t of mine) if (held.has(t.id)) t.expires += back;
};

/** How many more turrets the garrison key's hold fast lets the artificer keep. */
export const keyExtra = (h: Hero): number => (formFinal(h, 'garrisonKey') ? n(FIN('garrisonKey'), 'extra') : 0);

/**
 * The fallen master's staff and its forms: the leaps (the prong's more and softer), the wake's lines of fire and the
 * master's rain (every leap leaps again). Returns the leaps to throw, from where to where, at what damage.
 */
export const staffLeaps = (s: BattleState, h: Hero, at: { x: number; y: number }, blast: Enemy[], chains: number, damageBp: number, radius: number, range: number): { from: { x: number; y: number }; to: Enemy; bp: number }[] => {
  const prong = formB(h, 'fallenStaff');
  const more = prong ? n(B('fallenStaff'), 'chains') : 0;
  const bp = prong ? mulBp(damageBp, n(B('fallenStaff'), 'damageRelBp')) : damageBp;
  const beyond = enemiesNear(s, at.x, at.y, range, (o) => targetable(s, o) && !withinRange(o.x, o.y, at.x, at.y, radius))
    .sort((a, b) => dist2(a.x, a.y, at.x, at.y) - dist2(b.x, b.y, at.x, at.y) || a.id - b.id)
    .slice(0, chains + more);
  const out = beyond.map((to) => ({ from: at, to, bp }));
  if (formFinal(h, 'fallenStaff')) {
    const seen = new Set<number>([...blast.map((o) => o.id), ...beyond.map((o) => o.id)]);
    const r = n(FIN('fallenStaff'), 'range');
    for (const o of beyond) {
      let best: Enemy | null = null;
      for (const x of enemiesNear(s, o.x, o.y, r, (q) => targetable(s, q) && !seen.has(q.id))) if (!best || dist2(x.x, x.y, o.x, o.y) < dist2(best.x, best.y, o.x, o.y) || (dist2(x.x, x.y, o.x, o.y) === dist2(best.x, best.y, o.x, o.y) && x.id < best.id)) best = x;
      if (!best) continue;
      seen.add(best.id);
      out.push({ from: { x: o.x, y: o.y }, to: best, bp });
    }
  }
  return out;
};

/** The wake: a line of fire along a leap, from where it left to its target. */
export const staffWake = (s: BattleState, h: Hero, from: { x: number; y: number }, to: { x: number; y: number }, blow: number, dt: Zone['dt']): void => {
  const f = A('fallenStaff');
  zoneOf(s, h, { kind: 'fireLine', x: from.x, y: from.y, tx: to.x, ty: to.y, radius: n(f, 'width'), ticks: n(f, 'ticks'), every: n(f, 'every'), damage: mulBp(blow, n(f, 'damageBp')), ...(dt ? { dt } : {}) });
};

const fireLineTick = (s: BattleState, z: Zone): void => {
  if (z.ticks % z.every !== 0) return;
  for (const e of onLine(s, z.x, z.y, z.tx ?? z.x, z.ty ?? z.y, z.radius, true)) damageEnemy(s, e, z.damage, { pid: z.pid, ignoresArmor: true, type: z.dt ?? 'fire' });
};

/**
 * The chapel lamp's forms on its heal (`lampHeal`): the high lamp heals the second most hurt too, the ward of light keeps
 * what the heal had left over as a shield. `heal` is what it meant to heal, `got` what it did.
 */
export const lampForms = (s: BattleState, h: Hero, best: Hero, heal: number, got: number, range: number): void => {
  if (formA(h, 'chapelLamp')) {
    let second: Hero | null = null;
    let bh = 0;
    for (const o of s.heroes) {
      if (o.id === best.id || o.downTicks > 0 || o.stats.maxHp <= 0 || !withinRange(h.x, h.y, o.x, o.y, range)) continue;
      const miss = idiv((o.stats.maxHp - o.hp) * 10000, o.stats.maxHp);
      if (miss > bh) {
        bh = miss;
        second = o;
      }
    }
    if (second && healHero(s, second, mulBp(heal, n(A('chapelLamp'), 'secondBp')), h.pid) > 0) ev(s, { t: 'beam', x: best.x, y: best.y, tx: second.x, ty: second.y, kind: 'highLamp' });
  }
  if (formB(h, 'chapelLamp') && heal > got) {
    const f = B('chapelLamp');
    const ward = imin(heal - got, mulBp(best.stats.maxHp, n(f, 'capBp')));
    if (ward > 0 && (best.shield ?? 0) < ward) {
      raiseShield(best, ward, n(f, 'ticks'), false);
      ev(s, { t: 'boom', x: best.x, y: best.y, r: 500, kind: 'lightWard' });
    }
  }
};

/**
 * Lucerna: every fifth blow of the lamp's cleric is a beam through the line; it strikes and heals what it crosses.
 * Returns what it dealt to the target (for the lamp's heal), or -1 when this blow is a plain one.
 */
export const lucernaShot = (s: BattleState, h: Hero, e: Enemy, dmg: number): number => {
  if (!formFinal(h, 'chapelLamp')) return -1;
  const f = FIN('chapelLamp');
  h.shots = (h.shots ?? 0) + 1;
  if (h.shots % n(f, 'every') !== 0) return -1;
  const d = isqrt(dist2(h.x, h.y, e.x, e.y)) || 1;
  const L = n(f, 'length');
  const tx = h.x + idiv((e.x - h.x) * L, d);
  const ty = h.y + idiv((e.y - h.y) * L, d);
  const w = n(f, 'width');
  let dealt = 0;
  for (const o of onLine(s, h.x, h.y, tx, ty, w)) {
    const got = damageEnemy(s, o, mulBp(dmg, n(f, 'damageBp')), { pid: h.pid, type: 'holy', ignoresArmor: true });
    if (o.id === e.id) dealt = got;
  }
  for (const o of s.heroes) if (o.id !== h.id && o.downTicks === 0 && onSegment(o.x, o.y, h.x, h.y, tx, ty, w)) healHero(s, o, mulBp(dmg, n(f, 'healBp')), h.pid);
  ev(s, { t: 'beam', x: h.x, y: h.y, tx, ty, kind: 'lucerna' });
  return dealt;
};

/** The watchtower bow, as carried: how often an arrow goes through, how hard, and its mark. */
export const bowNumbers = (h: Hero, every: number, damageBp: number, markBp: number, markTicks: number): [number, number, number, number] => {
  if (formB(h, 'watchBow')) {
    every = n(B('watchBow'), 'every');
    damageBp = mulBp(damageBp, n(B('watchBow'), 'damageRelBp'));
  }
  if (formA(h, 'watchBow')) {
    markBp += n(A('watchBow'), 'markAddBp');
    markTicks = mulBp(markTicks, n(A('watchBow'), 'markTicksBp'));
  }
  return [every, damageBp, markBp, markTicks];
};

/** The watchtower's lance: the arrow through the line pins the last it reached (elites too, bosses never). */
export const bowLance = (s: BattleState, h: Hero, through: Enemy[]): void => {
  if (!formFinal(h, 'watchBow')) return;
  let last: Enemy | null = null;
  let ld = -1;
  for (const e of through) {
    const d = dist2(e.x, e.y, h.x, h.y);
    if (e.hp > 0 && (d > ld || (d === ld && last && e.id < last.id))) {
      ld = d;
      last = e;
    }
  }
  if (!last || !holdable(last) || formBoss(s, last) || stunFor({ ...last, elite: false }, 3) === 0) return;
  const st = n(FIN('watchBow'), 'stun');
  if (last.stun < st) last.stun = st;
  ev(s, { t: 'boom', x: last.x, y: last.y, r: 400, kind: 'lance' });
};

/** The wall axe's whirl, as carried: how far it pulls (the anchor's twice). */
export const axeRadius = (h: Hero, r: number): number => (formA(h, 'wallAxe') ? mulBp(r, n(A('wallAxe'), 'radiusBp')) : r);

/** The eye of the hurricane: whether the whirl pulls this one too (bosses and flyers; never the dragon). */
export const axeEye = (h: Hero, e: Enemy): boolean => formFinal(h, 'wallAxe') && e.kind !== 'boneWall' && e.kind !== 'dragon';

/** The eye: a boss is pulled a hex at most, a flyer anywhere and held on the ground. Returns whether it moved. */
export const axePullEye = (s: BattleState, h: Hero, e: Enemy, to: number): boolean => {
  const f = FIN('wallAxe');
  const d = isqrt(dist2(e.x, e.y, h.x, h.y));
  if (d <= to) return false;
  const boss = formBoss(s, e) || (!e.elite && stunFor(e, 3) === 0);
  const step = boss ? imin(n(f, 'bossPull'), d - to) : d - to;
  const x = e.x + idiv((h.x - e.x) * step, d);
  const y = e.y + idiv((h.y - e.y) * step, d);
  const flying = isFlying(e);
  if (!flying && !heroWalkable(s, cellAtWorld(s, x, y))) return false;
  e.x = x;
  e.y = y;
  e.next = -1;
  if (flying) e.grounded = n(f, 'ground');
  return true;
};

/** After the whirl pulled `pulled`: the anchor holds the barbarian, the blood swirl heals it. */
export const axeAfter = (s: BattleState, h: Hero, pulled: number): void => {
  if (formA(h, 'wallAxe')) {
    h.anchor = n(A('wallAxe'), 'steady');
    ev(s, { t: 'boom', x: h.x, y: h.y, r: axeRadius(h, n(UNIQUES.wallAxe.fx, 'radius')), kind: 'anchor' });
  }
  if (formB(h, 'wallAxe') && pulled > 0) {
    healHero(s, h, mulBp(h.stats.maxHp, n(B('wallAxe'), 'healBp') * pulled), h.pid);
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 900, kind: 'bloodSwirl' });
  }
};

/** Ser Gaspar's hammer, the toll: the smitten rings while it stays stunned. */
export const smiteToll = (s: BattleState, h: Hero, e: Enemy): void => {
  if (e.hp <= 0 || e.stun <= 0) return;
  const f = A('gasparHammer');
  zoneOf(s, h, { kind: 'toll', x: e.x, y: e.y, fe: e.id, radius: n(f, 'radius'), ticks: e.stun, slowBp: n(f, 'slowBp') });
};

const tollTick = (s: BattleState, z: Zone): void => {
  const e = s.enemies.find((o) => o.id === z.fe);
  if (!e || e.hp <= 0 || e.stun <= 0) {
    z.ticks = 0;
    return;
  }
  z.x = e.x;
  z.y = e.y;
  for (const o of enemiesNear(s, z.x, z.y, z.radius, (x) => x.id !== e.id)) slowFor(o, z.slowBp, 3);
  if (z.ticks % 12 === 0) ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'toll' });
};

/** The heavy hammer: the smitten takes more holy while it stays stunned. */
export const smiteHeavy = (s: BattleState, e: Enemy): void => {
  if (e.hp <= 0 || e.stun <= 0) return;
  e.holyT = s.tick + e.stun;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 700, kind: 'heavySmite' });
};

/** How much more holy a nightmare under the heavy hammer takes (bp). */
export const holyTakenBp = (s: BattleState, e: Enemy): number => ((e.holyT ?? 0) > s.tick ? n(B('gasparHammer'), 'holyBp') : 10000);

/** Gaspar's guard: a smite that kills calls a spectre of the old garrison (Ser Gaspar himself, if the group heard him). */
export const gasparGuard = (s: BattleState, h: Hero, e: Enemy, blow: number): void => {
  if (e.hp > 0) return;
  const f = FIN('gasparHammer');
  const bots = (s.bots ??= []);
  s.bots = bots.filter((b) => b.owner !== h.id || b.kind !== 'gasparGuard');
  const hp = mulBp(h.stats.maxHp, n(f, 'hpBp'));
  s.bots.push({
    id: s.nextId++,
    owner: h.id,
    pid: h.pid,
    kind: 'gasparGuard',
    x: e.x,
    y: e.y,
    hp,
    maxHp: hp,
    damage: mulBp(blow, n(f, 'damageBp')),
    radius: n(f, 'radius'),
    every: n(f, 'every'),
    cd: 0,
    expires: s.tick + n(f, 'ticks'),
    taunt: n(f, 'taunt'),
    aura: 0,
    auraBp: 0,
    facing: h.facing,
    anim: 'idle',
    animT: 0,
    ...(formBits(h, 'gasparHammer') & FORM_BIT.gaspar ? { gaspar: true } : {}),
  } satisfies Bot);
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'gasparGuard' });
};

/**
 * The old root staff's forms on what the roots hold (`held`, `ticks` long): the bitter sap's poison, the deep root's
 * elites held twice as long, the old forest's mark (what dies held leaves a root).
 */
export const rootsHeld = (s: BattleState, h: Hero, held: Enemy[], ticks: number, blow: number): void => {
  const a = formA(h, 'oldRootStaff');
  const b = formB(h, 'oldRootStaff');
  const fin = formFinal(h, 'oldRootStaff');
  if (!a && !b && !fin) return;
  for (const e of held) {
    if (e.hp <= 0) continue;
    if (a) {
      const f = A('oldRootStaff');
      const dmg = imax(1, mulBp(blow, n(f, 'damageBp')));
      const every = n(f, 'every');
      // Never over a stronger burn or poison already on it (by what it does a tick).
      const stronger = e.dotTicks > 0 && e.dotEvery > 0 && idiv(e.dotDmg * 1000, e.dotEvery) > idiv(dmg * 1000, every);
      if (!stronger) {
        e.dotDmg = dmg;
        e.dotType = 'poison';
        e.dotEvery = every;
        e.dotTicks = n(f, 'ticks');
        e.lastPid = h.pid;
        e.wasps = undefined;
      }
    }
    if (b && e.elite) {
      const t = mulBp(ticks, n(B('oldRootStaff'), 'eliteBp'));
      if (e.slowTicks < t) e.slowTicks = t;
    }
    if (fin) {
      e.rootBy = h.id;
      e.rootT = s.tick + (b && e.elite ? mulBp(ticks, n(B('oldRootStaff'), 'eliteBp')) : ticks);
    }
  }
  if (a) ev(s, { t: 'boom', x: h.x, y: h.y, r: 0, kind: 'bitterSap' });
};

/** How much longer the deep root holds (ticks). */
export const rootsMore = (h: Hero): number => (formB(h, 'oldRootStaff') ? n(B('oldRootStaff'), 'ticks') : 0);

/** The old forest: a nightmare that died held leaves a root on the ground. */
const forestRoot = (s: BattleState, e: Enemy): void => {
  if (e.rootBy === undefined || (e.rootT ?? 0) < s.tick) return;
  const h = heroById(s, e.rootBy);
  if (!h || !formFinal(h, 'oldRootStaff')) return;
  const f = FIN('oldRootStaff');
  const mine = s.zones.filter((z) => z.kind === 'oldRoot' && z.pid === h.pid);
  if (mine.length >= n(f, 'max')) {
    const old = mine[0] as Zone;
    s.zones = s.zones.filter((z) => z.id !== old.id);
  }
  zoneOf(s, h, { kind: 'oldRoot', x: e.x, y: e.y, radius: n(f, 'radius'), ticks: n(f, 'ticks'), stun: n(f, 'hold') });
  ev(s, { t: 'boom', x: e.x, y: e.y, r: n(f, 'radius'), kind: 'oldRoot' });
};

const oldRootTick = (s: BattleState, z: Zone): void => {
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o) && holdable(o))) {
    slowFor(e, 10000, z.stun ?? 0);
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'rootSnap' });
    z.ticks = 0;
    return;
  }
};

/* ———————————————————————— armours ———————————————————————— */

/** The last watch's parapet: while challenging, the allies by the warrior take less (a ground that walks with it). */
export const parapet = (s: BattleState, h: Hero, ticks: number): void => {
  const f = A('lastWatch');
  s.zones = s.zones.filter((z) => z.kind !== 'parapet' || z.follow !== h.id);
  zoneOf(s, h, { kind: 'parapet', x: h.x, y: h.y, radius: n(f, 'radius'), ticks, follow: h.id, wardBp: n(f, 'takenBp') });
};

const parapetTick = (s: BattleState, z: Zone): void => {
  const w = z.follow === undefined ? undefined : heroById(s, z.follow);
  if (!w || w.downTicks > 0 || w.taunt <= 0) {
    z.ticks = 0;
    return;
  }
  for (const o of s.heroes) if (o.id !== w.id && o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) wardHero(o, z.wardBp ?? 10000, 3);
};

/** A blow struck back by the last watch while challenging: the vengeance gathers it. */
export const watchStruckBack = (h: Hero, back: number): void => {
  if (back > 0 && formB(h, 'lastWatch')) h.venge = (h.venge ?? 0) + back;
};

/**
 * About to fall (hp at 0 or below): the last turn (challenging), unfallen (in a frenzy). True when it holds at 1.
 * Before the oath's undying and anything else that keeps a hero up.
 */
export const formsHoldUp = (s: BattleState, h: Hero, was: number): boolean => {
  if (!h.uqForm) return false;
  if (h.taunt > 0 && formFinal(h, 'lastWatch') && (h.turnAt === undefined || s.tick - h.turnAt >= n(FIN('lastWatch'), 'every'))) {
    const f = FIN('lastWatch');
    h.turnAt = s.tick;
    h.hp = 1;
    h.taunt += n(f, 'more');
    for (const e of enemiesNear(s, h.x, h.y, h.tauntRadius, (o) => o.kind !== 'boneWall' && o.taunter === h.id)) e.tauntTicks = imax(e.tauntTicks ?? 0, n(f, 'more'));
    for (const z of s.zones) if (z.kind === 'parapet' && z.follow === h.id) z.ticks += n(f, 'more');
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 1200, kind: 'lastTurn' });
    return true;
  }
  if ((h.frenzy ?? 0) > 0 && formFinal(h, 'boarHide')) {
    h.hp = 1;
    if (was > 1) ev(s, { t: 'boom', x: h.x, y: h.y, r: 900, kind: 'unfallen' });
    return true;
  }
  return false;
};

/** A blow taken from `from`: the counterfrost chills it, the bear's hug holds it. */
export const formsStruck = (s: BattleState, h: Hero, from: Enemy): void => {
  if (!h.uqForm) return;
  if ((h.mantleUp ?? 0) > s.tick && formB(h, 'frostMantle')) {
    chill(s, from);
    ev(s, { t: 'boom', x: from.x, y: from.y, r: 350, kind: 'counterFrost' });
  }
  if ((h.bear ?? 0) > 0 && formB(h, 'dwarfBark') && from.hp > 0) {
    const f = B('dwarfBark');
    if (from.hugAt === undefined || s.tick - from.hugAt >= n(f, 'every')) {
      from.hugAt = s.tick;
      if (holdable(from)) slowFor(from, 10000, n(f, 'hold'));
      ev(s, { t: 'boom', x: from.x, y: from.y, r: 400, kind: 'hug' });
    }
  }
};

/** The anvil harness's automaton by its maker: the iron guard. */
export const anvilGuard = (s: BattleState, b: Bot, o: Hero): void => {
  if (!formA(o, 'anvilHarness')) return;
  const f = A('anvilHarness');
  if (withinRange(b.x, b.y, o.x, o.y, n(f, 'radius'))) wardHero(o, n(f, 'takenBp'), 3);
};

/** The forge: the automaton's burst leaves the ground burning and it is built again once. Returns it rebuilt, or null. */
export const anvilForge = (s: BattleState, b: Bot): Bot | null => {
  const o = heroById(s, b.owner);
  if (!o || !formB(o, 'anvilHarness')) return null;
  const f = B('anvilHarness');
  zoneOf(s, o, { kind: 'burn', x: b.x, y: b.y, radius: b.burst?.radius ?? 1500, ticks: n(f, 'ticks'), every: n(f, 'every'), damage: mulBp(n(f, 'damage'), o.stats.powerBp), ...(b.dt ? { dt: b.dt } : {}) });
  if (b.rebuilt || b.expires <= s.tick) return null;
  ev(s, { t: 'boom', x: b.x, y: b.y, r: 900, kind: 'forgeRebuild' });
  return { ...b, id: s.nextId++, hp: b.maxHp, cd: 0, rebuilt: true, anim: 'idle', animT: 0 };
};

/** The living anvil: an automaton that lasts until it breaks. */
export const ANVIL_FOREVER = 0x3fffffff;

/** The frost mantle, as carried: whether its ring rises now (the winter's crowd too), and how wide. */
export const mantleWakes = (s: BattleState, h: Hero, low: boolean): boolean => {
  if (low) return true;
  if (!formFinal(h, 'frostMantle')) return false;
  const f = FIN('frostMantle');
  return enemiesNear(s, h.x, h.y, n(f, 'radius'), (o) => targetable(s, o) && o.kind !== 'boneWall').length >= n(f, 'around');
};
export const mantleRadius = (h: Hero, r: number): number => (formA(h, 'frostMantle') ? mulBp(r, n(A('frostMantle'), 'radiusBp')) : r);
/** The glacier freezes elites too, a third (bosses never). */
export const mantleFreeze = (s: BattleState, h: Hero, e: Enemy, stun: number): number => {
  // A freeze is frost: on the dragon of the Burn it is no dry blow (docs/UNICAS.md §4.3).
  if (e.kind === 'dragon') return 0;
  const st = stunFor(e, stun);
  if (st > 0 || !formA(h, 'frostMantle') || !e.elite || !holdable(e) || formBoss(s, e)) return st;
  return stunFor({ ...e, elite: false }, 3) > 0 ? idiv(stun, 3) : 0;
};
/** The ring stood up: the counterfrost chills whoever strikes the mage while it stands. */
export const mantleRose = (s: BattleState, h: Hero, ticks: number): void => {
  if (formB(h, 'frostMantle')) h.mantleUp = s.tick + ticks;
};

/** The wanderer's cloak, before the leap back: the empty cloak stays where the ranger stood. */
export const cloakLeft = (s: BattleState, h: Hero): void => {
  if (!formA(h, 'wandererCloak')) return;
  const f = A('wandererCloak');
  const bots = (s.bots ??= []);
  s.bots = bots.filter((b) => b.owner !== h.id || b.kind !== 'cloak');
  const hp = mulBp(h.stats.maxHp, n(f, 'hpBp'));
  s.bots.push({ id: s.nextId++, owner: h.id, pid: h.pid, kind: 'cloak', x: h.x, y: h.y, hp, maxHp: hp, damage: 0, radius: 0, every: 0, cd: 0, expires: s.tick + n(f, 'ticks'), taunt: n(f, 'taunt'), aura: 0, auraBp: 0, facing: h.facing, anim: 'idle', animT: 0 } satisfies Bot);
};

/** After the leap back: the lost trail hides the ranger and readies a critical. */
export const cloakLanded = (s: BattleState, h: Hero): void => {
  if (!formFinal(h, 'wandererCloak')) return;
  const t = n(FIN('wandererCloak'), 'hidden');
  if ((h.hidden ?? 0) < t) h.hidden = t;
  h.sureCrit = true;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 500, kind: 'lostTrail' });
};

/** The boar hide's frenzy started by itself: cold blood wards, the boar charges the biggest around. */
export const boarWoke = (s: BattleState, h: Hero): void => {
  if (formA(h, 'boarHide')) {
    wardHero(h, n(A('boarHide'), 'takenBp'), h.frenzy ?? 0);
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'coldBlood' });
  }
  if (!formB(h, 'boarHide')) return;
  const f = B('boarHide');
  let big: Enemy | null = null;
  for (const e of enemiesNear(s, h.x, h.y, n(f, 'range'), (o) => targetable(s, o) && o.kind !== 'boneWall')) if (!big || e.maxHp > big.maxHp || (e.maxHp === big.maxHp && e.id < big.id)) big = e;
  if (!big) return;
  const d = isqrt(dist2(h.x, h.y, big.x, big.y)) || 1;
  const lx = big.x + idiv((h.x - big.x) * 600, d);
  const ly = big.y + idiv((h.y - big.y) * 600, d);
  const fx0 = h.x;
  const fy0 = h.y;
  if (d > 900 && heroWalkable(s, cellAtWorld(s, lx, ly))) {
    h.x = h.destX = h.ax = lx;
    h.y = h.destY = h.ay = ly;
    h.path = [];
    h.moving = false;
  }
  damageEnemy(s, big, mulBp(h.stats.attack, n(f, 'damageBp')), { pid: h.pid });
  const st = heavy(big, n(f, 'stun'));
  if (big.stun < st) big.stun = st;
  ev(s, { t: 'beam', x: fx0, y: fy0, tx: h.x, ty: h.y, kind: 'boarCharge' });
  ev(s, { t: 'boom', x: big.x, y: big.y, r: 700, kind: 'boarCharge' });
};

/** The oath plate's aegis broke on its paladin: the relay, the steel echo (on those it taunted), the steel oath. */
export const aegisBroke = (s: BattleState, h: Hero, taunted: Enemy[]): void => {
  if (!h.uqForm) return;
  if (formA(h, 'oathPlate') && h.aegisAmt) {
    const f = A('oathPlate');
    let best: Hero | null = null;
    let bm = -1;
    for (const o of s.heroes) {
      if (o.id === h.id || o.downTicks > 0 || o.stats.maxHp <= 0 || !withinRange(h.x, h.y, o.x, o.y, n(f, 'range'))) continue;
      const miss = idiv((o.stats.maxHp - o.hp) * 10000, o.stats.maxHp);
      if (miss > bm) {
        bm = miss;
        best = o;
      }
    }
    if (best) {
      raiseShield(best, mulBp(h.aegisAmt, n(f, 'shareBp')), SKILLS.aegis.duration, false);
      ev(s, { t: 'beam', x: h.x, y: h.y, tx: best.x, ty: best.y, kind: 'relay' });
    }
  }
  if (formB(h, 'oathPlate')) {
    const st = n(B('oathPlate'), 'stun');
    for (const e of taunted) {
      const t = heavy(e, st);
      if (e.stun < t) e.stun = t;
    }
    ev(s, { t: 'boom', x: h.x, y: h.y, r: n(UNIQUES.oathPlate.fx, 'radius'), kind: 'steelEcho' });
  }
  if (formFinal(h, 'oathPlate') && h.steelAt === undefined) h.steelAt = s.tick + n(FIN('oathPlate'), 'after');
};

/** The aegis was cast by the oath plate's paladin: what it holds, for the relay and the steel oath (which it re-arms). */
export const aegisCast = (h: Hero, amount: number): void => {
  if (!h.uqForm || !formBits(h, 'oathPlate')) return;
  h.aegisAmt = amount;
  h.steelAt = undefined;
};

/** The bear form began: the old bear will stay when it ends. */
export const bearBegan = (h: Hero): void => {
  if (formFinal(h, 'dwarfBark')) h.barkOn = true;
};

/* ———————————————————————— amulets ———————————————————————— */

/** The postern medal, as carried: how far the oath reaches its allies (everyone with the vow of all), and how long it keeps them up. */
export const oathReach = (h: Hero, r: number): number => (formA(h, 'posternMedal') ? 0x3fffffff : formB(h, 'posternMedal') ? mulBp(r, n(B('posternMedal'), 'reachBp')) : r);
export const oathHolds = (h: Hero, t: number): number => (formB(h, 'posternMedal') ? mulBp(t, n(B('posternMedal'), 'durationBp')) : t);

/** A hero went down: the medal calls the oath of a warrior who has it ready; what a form gathered for this life goes. */
export const formsDown = (s: BattleState, h: Hero): void => {
  if (h.uqForm) {
    h.venge = undefined;
    h.barkOn = undefined;
  }
  for (const w of s.heroes) {
    if (w.id === h.id || w.downTicks > 0 || !formFinal(w, 'posternMedal') || w.medalCall) continue;
    const slot = kitSlot(w, 'oath');
    if (slot < 0 || (w.skillCd[slot] ?? 1) > 0) continue;
    if (w.medalAt !== undefined && s.tick - w.medalAt < n(FIN('posternMedal'), 'every')) continue;
    if (!withinRange(w.x, w.y, h.x, h.y, oathReach(w, SKILLS.oath.radius))) continue;
    w.medalCall = true;
  }
};

/** The slot a hero carries a skill in (-1: not carried). */
export const kitSlot = (h: Hero, id: SkillId): number => {
  const kit = h.kit ?? HEROES[h.cls].skills;
  return kit.indexOf(id);
};

/** The grenade, as carried: Maelis's kit's pair (`powerBp` each), and every third one splitting in three (the flasks). */
export const kitSpots = (h: Hero, p: { x: number; y: number }, count = true): { x: number; y: number }[] | null => {
  const pair = formB(h, 'maelisKit');
  let spots: { x: number; y: number }[] | null = null;
  if (pair) {
    const g = idiv(n(B('maelisKit'), 'gap'), 2);
    spots = [
      { x: p.x - g, y: p.y },
      { x: p.x + g, y: p.y },
    ];
  }
  // (An echo's grenade is the same grenade again: it never counts toward the third.)
  if (formFinal(h, 'maelisKit') && count) {
    const f = FIN('maelisKit');
    h.flaskN = (h.flaskN ?? 0) + 1;
    if (h.flaskN % n(f, 'every') === 0) {
      const gap = n(f, 'gap');
      spots = [0, 341, 682].map((a) => ({ x: p.x + idiv(cosA(a) * gap, 10000), y: p.y + idiv(sinA(a) * gap, 10000) }));
    }
  }
  return spots;
};

/** The puddle the grenade leaves (Maelis's kit, A): it heals what stands on it a while. */
const puddleAfter = (s: BattleState, z: Zone): void => {
  const f = A('maelisKit');
  zoneOf(s, z, { kind: 'puddle', x: z.x, y: z.y, radius: z.radius, ticks: n(f, 'ticks'), every: n(f, 'every'), heal: mulBp(z.heal ?? 0, n(f, 'healBp')) });
};

const puddleTick = (s: BattleState, z: Zone): void => {
  if (z.ticks % z.every !== 0) return;
  for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) healHero(s, o, z.heal ?? 0, z.pid);
};

/** The rosary's dawn, as carried: reaching the whole party (not with the early dawn), and how far otherwise. */
export const dawnEverywhere = (h: Hero): boolean => !formB(h, 'healerRosary');
export const dawnReach = (h: Hero, r: number): number => (formB(h, 'healerRosary') ? imax(r, n(B('healerRosary'), 'reach')) : r);
/** One the dawn reached (raised: it was down): the guarding dawn's aegis, the second dawn's watch. */
export const dawnTouched = (s: BattleState, h: Hero, o: Hero, raised: boolean): void => {
  if (raised && formA(h, 'healerRosary')) {
    const f = A('healerRosary');
    raiseShield(o, mulBp(o.stats.maxHp, n(f, 'shieldBp')), n(f, 'ticks'), false);
    ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'guardingDawn' });
  }
  if (formFinal(h, 'healerRosary')) {
    const f = FIN('healerRosary');
    if ((o.intercede ?? 0) < n(f, 'ticks')) {
      o.intercede = n(f, 'ticks');
      o.intercedeBp = n(f, 'hpBp');
      ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'secondDawn' });
    }
  }
};

/** Rain, storm, hallowed ground and bloom carry their piece's form bits (laid by their programs). */
const zoneForm = (z: Zone): number => z.fm ?? 0;

/** A zone that follows its caster moved with it: the still eye counts the ticks the mage stood. */
export const followed = (z: Zone, h: Hero): void => {
  if (z.kind === 'storm' && zoneForm(z) & FORM_BIT.a) z.still = z.x === h.x && z.y === h.y ? (z.still ?? 0) + 1 : 0;
};

/** A storm's pulse, as its eye carries it: harder when the mage stood still. */
export const stormBlow = (z: Zone): number => (zoneForm(z) & FORM_BIT.a && (z.still ?? 0) >= n(A('stormEye'), 'still') ? mulBp(z.damage, n(A('stormEye'), 'damageBp')) : z.damage);

/** After a storm's pulse: the leaping bolt strikes one more beyond it. */
export const stormLeap = (s: BattleState, z: Zone, blow: number): void => {
  if (!(zoneForm(z) & FORM_BIT.b)) return;
  const range = n(B('stormEye'), 'range');
  let best: Enemy | null = null;
  for (const e of enemiesNear(s, z.x, z.y, z.radius + range, (o) => targetable(s, o) && !withinRange(o.x, o.y, z.x, z.y, z.radius)))
    if (!best || dist2(e.x, e.y, z.x, z.y) < dist2(best.x, best.y, z.x, z.y) || (dist2(e.x, e.y, z.x, z.y) === dist2(best.x, best.y, z.x, z.y) && e.id < best.id)) best = e;
  if (!best) return;
  damageEnemy(s, best, blow, { pid: z.pid, ignoresArmor: true, type: z.dt ?? 'lightning' });
  ev(s, { t: 'beam', x: z.x, y: z.y, tx: best.x, ty: best.y, kind: 'stormJump' });
};

/** A tick of the rain (before its pulse), as the whistle's forms carry it: the spikes pin who steps in, the rain follows the biggest. */
export const rainTick = (s: BattleState, z: Zone): void => {
  const fm = zoneForm(z);
  if (fm & FORM_BIT.b) {
    let e = z.fe === undefined ? undefined : s.enemies.find((o) => o.id === z.fe && o.hp > 0);
    if (!e && z.ticks % 6 === 0) {
      let big: Enemy | null = null;
      for (const o of enemiesNear(s, z.x, z.y, z.radius, (x) => targetable(s, x))) if (!big || o.maxHp > big.maxHp || (o.maxHp === big.maxHp && o.id < big.id)) big = o;
      if (big) {
        z.fe = big.id;
        e = big;
      }
    }
    if (e) {
      z.x = e.x;
      z.y = e.y;
    }
  }
  if (fm & FORM_BIT.a) {
    const hit = (z.hit ??= []);
    const st = n(A('mossWhistle'), 'stun');
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o))) {
      if (hit.includes(e.id)) continue;
      hit.push(e.id);
      const t = stunFor(e, st);
      if (t > 0 && e.stun < t) {
        e.stun = t;
        ev(s, { t: 'boom', x: e.x, y: e.y, r: 300, kind: 'spikes' });
      }
    }
  }
};

/** The rain killed `e`: the closed sky counts, and the fifth brings a second, smaller rain down there. */
export const rainKilled = (s: BattleState, z: Zone, e: Enemy): void => {
  if (!(zoneForm(z) & FORM_BIT.final) || (z.kills ?? 0) < 0) return;
  const f = FIN('mossWhistle');
  z.kills = (z.kills ?? 0) + 1;
  if (z.kills < n(f, 'kills')) return;
  z.kills = -1;
  zoneOf(s, z, {
    kind: 'rain',
    x: e.x,
    y: e.y,
    radius: mulBp(z.radius, n(f, 'radiusBp')),
    ticks: mulBp(SKILLS.arrowRain.duration, n(f, 'ticksBp')),
    every: z.every,
    damage: z.damage,
    slowBp: z.slowBp,
    ...(z.reveal ? { reveal: true } : {}),
    ...(z.dt ? { dt: z.dt } : {}),
    ...(zoneForm(z) & FORM_BIT.a ? { fm: FORM_BIT.a } : {}),
  });
  ev(s, { t: 'boom', x: e.x, y: e.y, r: mulBp(z.radius, n(f, 'radiusBp')), kind: 'closedSky' });
};

/** The tail fang, as carried: the crescendo's blow on the `chain`th leap in a row, and the hunger's heal. */
export const fangBlowBp = (h: Hero): number => (formA(h, 'tailFang') ? 10000 + n(A('tailFang'), 'stepBp') * imin(h.fangChain ?? 0, n(A('tailFang'), 'max')) : 10000);
export const fangHealBp = (h: Hero): number => (formB(h, 'tailFang') && (h.fangChain ?? 0) > 0 ? n(B('tailFang'), 'healBp') : 10000);
/** What the tail fang's forms did on a leap, seen (events only): the crescendo's growing wave, the hunger's double heal, the endless leap's chain. */
export const fangSeen = (s: BattleState, h: Hero, cres: number, hunger: number, killed: boolean, struck: number): void => {
  if (formA(h, 'tailFang')) ev(s, { t: 'boom', x: h.x, y: h.y, r: mulBp(900, cres), kind: 'crescendo' });
  if (formB(h, 'tailFang') && hunger > 10000) ev(s, { t: 'boom', x: h.x, y: h.y, r: 800, kind: 'hunger' });
  if (formFinal(h, 'tailFang') && !killed && struck >= n(FIN('tailFang'), 'hits')) ev(s, { t: 'boom', x: h.x, y: h.y, r: 1000, kind: 'endless' });
};

/** A leap landed: whether the tail fang makes it ready again (a kill, or the endless leap's three struck), and the chain kept. */
export const fangAgain = (h: Hero, killed: boolean, struck: number): boolean => {
  const again = killed || (formFinal(h, 'tailFang') && struck >= n(FIN('tailFang'), 'hits'));
  if (formBits(h, 'tailFang') & (FORM_BIT.a | FORM_BIT.b)) h.fangChain = again ? (h.fangChain ?? 0) + 1 : 0;
  return again;
};

/** A pulse of hallowed ground on the inked: the blinding flame. */
export const hallowInked = (z: Zone, e: Enemy): void => {
  if (!(zoneForm(z) & FORM_BIT.b)) return;
  const bp = n(B('vigilCandle'), 'blindBp');
  // A stronger blinding (the paladin's light) stays as it is.
  if ((e.blind ?? 0) > 0 && (e.blindBp ?? 10000) < bp) return;
  if ((e.blind ?? 0) < z.every + 2) e.blind = z.every + 2;
  e.blindBp = bp;
};

/** A zone is over (its last tick): what the forms leave after hallowed ground, the bloom and the grenade. */
export const zoneEnded = (s: BattleState, z: Zone): void => {
  const fm = zoneForm(z);
  if (!fm) return;
  if (z.kind === 'hallow') {
    if (fm & FORM_BIT.a && !z.hop) {
      const f = A('vigilCandle');
      let best: Hero | null = null;
      let bn = 0;
      for (const o of s.heroes) {
        if (o.id === z.follow || o.downTicks > 0 || !withinRange(z.x, z.y, o.x, o.y, n(f, 'range'))) continue;
        const k = enemiesNear(s, o.x, o.y, n(f, 'beset'), (e) => targetable(s, e)).length;
        if (k > bn) {
          bn = k;
          best = o;
        }
      }
      if (best) {
        zoneOf(s, z, { kind: 'hallow', x: best.x, y: best.y, radius: z.radius, ticks: n(f, 'ticks'), every: z.every, damage: z.damage, slowBp: z.slowBp, follow: best.id, fm, hop: true, ...(z.heal ? { heal: z.heal } : {}), ...(z.dt ? { dt: z.dt } : {}) });
        ev(s, { t: 'beam', x: z.x, y: z.y, tx: best.x, ty: best.y, kind: 'leapFlame' });
        return;
      }
    }
    if (fm & FORM_BIT.final) zoneOf(s, z, { kind: 'vigil', x: z.x, y: z.y, radius: z.radius, ticks: n(FIN('vigilCandle'), 'ticks') });
    return;
  }
  if (z.kind === 'bloom' && fm & FORM_BIT.final) {
    const f = FIN('maelisSeed');
    const gap = n(f, 'gap');
    for (let i = 0; i < n(f, 'count'); i++) {
      const a = idiv(i * 1024, n(f, 'count'));
      zoneOf(s, z, { kind: 'sprout', x: z.x + idiv(cosA(a) * gap, 10000), y: z.y + idiv(sinA(a) * gap, 10000), radius: n(f, 'radius'), ticks: n(f, 'ticks'), every: 12, heal: mulBp(z.heal ?? 0, n(f, 'healBp')) });
    }
    ev(s, { t: 'boom', x: z.x, y: z.y, r: gap + n(f, 'radius'), kind: 'garden' });
    return;
  }
  if (z.kind === 'grenade' && fm & FORM_BIT.a && z.heal) puddleAfter(s, z);
};

/** A tick of the bloom, as the seed's forms carry it: the tangle holds who steps in, the pollen chills (each pulse). */
export const bloomTick = (s: BattleState, z: Zone): void => {
  const fm = zoneForm(z);
  if (fm & FORM_BIT.a) {
    const f = A('maelisSeed');
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o) && holdable(o))) {
      if (e.tangleAt !== undefined && s.tick - e.tangleAt < n(f, 'every')) continue;
      e.tangleAt = s.tick;
      slowFor(e, 10000, n(f, 'hold'));
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 350, kind: 'tangle' });
    }
  }
  if (fm & FORM_BIT.b && z.ticks % z.every === 0) for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o))) chill(s, e);
};

/** A tick of the sanctuary, as the dwarf mail's forms carry it: firm step for the heroes on it, the atrium for the nightmares. */
export const sanctuaryHero = (z: Zone, o: Hero): void => {
  if (!(zoneForm(z) & FORM_BIT.a)) return;
  if (o.shove) o.shove = 0;
  if (o.held) o.held = 0;
};
export const sanctuaryTick = (s: BattleState, z: Zone): void => {
  if (!(zoneForm(z) & FORM_BIT.b)) return;
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => !isFlying(o))) slowFor(e, n(B('dwarfMail'), 'slowBp'), 3);
};

/** The vigil: no nightmare stands inside it (bosses do); whoever steps in is put back at its edge. */
const vigilTick = (s: BattleState, z: Zone): void => {
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => o.hp > 0 && o.kind !== 'boneWall' && !isFlying(o))) {
    if (formBoss(s, e) || e.kind === 'dragon') continue;
    const d = isqrt(dist2(e.x, e.y, z.x, z.y));
    const out = z.radius + 60;
    const x = d > 0 ? z.x + idiv((e.x - z.x) * out, d) : z.x + out;
    const y = d > 0 ? z.y + idiv((e.y - z.y) * out, d) : z.y;
    moveTo(s, e, x, y);
  }
};

const sproutTick = (s: BattleState, z: Zone): void => {
  if (z.ticks % z.every !== 0) return;
  for (const o of s.heroes) if (o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius)) healHero(s, o, mulBp(o.stats.maxHp, z.heal ?? 0), z.pid);
};

/** The forms' own zones, each tick (skills.ts `updateZones`). True when the zone is one of them. */
export const formZone = (s: BattleState, z: Zone): boolean => {
  switch (z.kind) {
    case 'cleft':
      riftTick(s, z);
      return true;
    case 'fireLine':
      fireLineTick(s, z);
      return true;
    case 'toll':
      tollTick(s, z);
      return true;
    case 'oldRoot':
      oldRootTick(s, z);
      return true;
    case 'parapet':
      parapetTick(s, z);
      return true;
    case 'puddle':
      puddleTick(s, z);
      return true;
    case 'vigil':
      vigilTick(s, z);
      return true;
    case 'sprout':
      sproutTick(s, z);
      return true;
  }
  return false;
};

/** A nightmare died: the old forest's root where it fell. */
export const formsDied = (s: BattleState, e: Enemy): void => {
  if (e.rootBy !== undefined) forestRoot(s, e);
};

/**
 * Every tick, for a standing hero carrying forms (uniques.ts `uniqueHeroTick`): the anchor, the vengeance's burst,
 * the steel oath, the moss, the old bear, the medal's call.
 */
export const formsHeroTick = (s: BattleState, h: Hero, cast: (slot: 0 | 1 | 2) => boolean): void => {
  if (!h.uqForm) return;
  if ((h.anchor ?? 0) > 0) {
    h.anchor = (h.anchor ?? 1) - 1;
    if (h.shove) h.shove = 0;
  }
  if (h.venge !== undefined && h.taunt <= 0) {
    const f = B('lastWatch');
    const blow = mulBp(h.venge, n(f, 'shareBp'));
    h.venge = undefined;
    if (blow > 0) for (const e of enemiesNear(s, h.x, h.y, n(f, 'radius'), (o) => targetable(s, o))) damageEnemy(s, e, blow, { pid: h.pid, ignoresArmor: true });
    ev(s, { t: 'boom', x: h.x, y: h.y, r: n(f, 'radius'), kind: 'vengeance' });
  }
  if (h.steelAt !== undefined && h.steelAt >= 0 && s.tick >= h.steelAt) {
    h.steelAt = -1;
    if (h.aegisAmt) {
      raiseShield(h, h.aegisAmt, SKILLS.aegis.duration, false);
      h.shieldTaunt = true;
      ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'steelOath' });
    }
  }
  if ((h.bear ?? 0) > 0 && s.tick % 12 === 0 && formA(h, 'dwarfBark')) healHero(s, h, mulBp(h.stats.maxHp, n(A('dwarfBark'), 'healBp')), h.pid);
  if (h.barkOn && !((h.bear ?? 0) > 0)) {
    h.barkOn = undefined;
    const f = FIN('dwarfBark');
    const hp = mulBp(h.stats.maxHp, n(f, 'hpBp'));
    const bots = (s.bots ??= []);
    s.bots = bots.filter((b) => b.owner !== h.id || b.kind !== 'barkBear');
    s.bots.push({ id: s.nextId++, owner: h.id, pid: h.pid, kind: 'barkBear', x: h.x + 400, y: h.y, hp, maxHp: hp, damage: h.stats.attack, radius: n(f, 'radius'), every: n(f, 'every'), cd: 0, expires: s.tick + n(f, 'ticks'), taunt: n(f, 'taunt'), aura: 0, auraBp: 0, facing: h.facing, anim: 'idle', animT: 0 } satisfies Bot);
    ev(s, { t: 'boom', x: h.x + 400, y: h.y, r: 900, kind: 'oldBear' });
  }
  if (h.medalCall) {
    h.medalCall = undefined;
    const slot = kitSlot(h, 'oath');
    if (slot >= 0 && cast(slot as 0 | 1 | 2)) {
      h.medalAt = s.tick;
      ev(s, { t: 'boom', x: h.x, y: h.y, r: 1200, kind: 'medal' });
    }
  }
};
